// src/constants/profileConfig.js
// Centralized profile configuration for the portfolio website.
// Update the fields below to reflect your personal and professional details.

const profileConfig = {
  // Basic Information
  fullName: "Dr. Jaganathan", // Your full name
  displayName: "Dr. Jaganathan", // Display/username for site-wide use
  title: "PhD in Chemistry | Research Scientist", // Professional title
  location: "Chennai, India", // City, Country
  organization: "Dr. Mohan's Diabetes Specialities Centre", // Current organization

  // Contact Details
  email: "jaganathan@drmohans.com", // Professional email
  phone: "+91 98765 43210", // Optional phone number

  // Social Links (add or remove as needed)
  socialLinks: [
    {
      type: "linkedin",
      url: "https://linkedin.com/in/drjaganathan",
      label: "LinkedIn"
    },
    {
      type: "researchgate",
      url: "https://www.researchgate.net/profile/Jaganathan",
      label: "ResearchGate"
    },
    {
      type: "googleScholar",
      url: "https://scholar.google.com/citations?user=jaganathan",
      label: "Google Scholar"
    },
    {
      type: "email",
      url: "mailto:jaganathan@drmohans.com",
      label: "Email"
    }
  ],

  // Short Bio
  bio: `I am a Chemistry researcher specializing in analytical and organic chemistry, with a focus on developing diagnostic and therapeutic solutions for diabetes and metabolic disorders. Currently, I lead research at Dr. Mohan's Diabetes Specialities Centre, collaborating with clinicians and scientists to translate chemical discoveries into healthcare impact.`,

  // About/Professional Info
  about: `With over a decade of experience in Chemistry research, I have contributed to biomarker discovery, assay development, and translational research in healthcare. My work bridges fundamental chemistry and clinical applications, aiming to improve patient outcomes in India and beyond.`,

  // Education (for About page)
  education: [
    {
      degree: "PhD in Chemistry",
      institution: "Indian Institute of Technology Madras",
      year: "2021",
      thesis: "Development of Novel Biomarkers for Early Detection of Diabetic Complications"
    },
    {
      degree: "MSc in Chemistry",
      institution: "University of Madras",
      year: "2016",
      thesis: "Synthesis of Heterocyclic Compounds for Pharmaceutical Applications"
    },
    {
      degree: "BSc in Chemistry",
      institution: "Stella Maris College, Chennai",
      year: "2014"
    }
  ],

  // Areas of Expertise (for About page)
  expertise: [
    "Analytical Chemistry",
    "Organic Synthesis",
    "Biomarker Discovery",
    "Spectroscopy & Chromatography",
    "Clinical Chemistry",
    "Translational Research",
    "Scientific Writing & Mentoring"
  ],

  // Professional Photo (place your image in public/images/ and update the path)
  profileImage: "/images/profile_jaganathan.jpg",

  // Current Research Focus
  research: {
    focus: "Application of advanced chemical techniques to address challenges in diabetes care, including biomarker discovery and point-of-care diagnostics.",
    topics: [
      "Biomarker Discovery in Diabetes",
      "Point-of-Care Diagnostics",
      "Therapeutic Monitoring in Diabetes Management"
    ],
    description: `Currently investigating the identification and validation of novel chemical biomarkers for early detection of diabetic complications, and developing rapid diagnostic assays for clinical use.`,
    methodologies: [
      "Spectroscopy Techniques (NMR, IR, UV-Vis)",
      "Chromatography Methods (HPLC, GC, LC-MS)",
      "Molecular Docking & Computational Chemistry",
      "Analytical Chemistry Techniques",
      "Electrochemical Analysis",
      "Sample Preparation & Extraction Methods"
    ]
  },

  // Published Papers
  publications: [
    {
      id: "pub1",
      title: "Novel Urinary Biomarkers for Early Detection of Diabetic Nephropathy",
      abstract: "Discovery and clinical validation of new urinary biomarkers using HPLC-MS for early detection of diabetic nephropathy in South Indian populations.",
      journal: "Journal of Clinical Chemistry & Laboratory Medicine",
      year: 2023,
      link: "https://doi.org/10.1016/j.jcclm.2023.05.001",
      authors: "Jaganathan J, Kumar S, et al.",
      citations: 12
    },
    {
      id: "pub2",
      title: "Development of a Point-of-Care Colorimetric Assay for Glucose Monitoring",
      abstract: "A rapid, low-cost colorimetric assay for glucose detection in blood samples, validated in a clinical cohort at Dr. Mohan's Diabetes Specialities Centre.",
      journal: "Analytical Methods in Chemistry",
      year: 2022,
      link: "https://doi.org/10.1155/2022/1234567",
      authors: "Jaganathan J, Singh R, et al.",
      citations: 8
    },
    {
      id: "pub3",
      title: "Organic Synthesis of Heterocyclic Compounds with Antidiabetic Potential",
      abstract: "Synthesis and biological evaluation of novel heterocyclic compounds exhibiting antidiabetic activity in vitro.",
      journal: "Indian Journal of Chemistry B",
      year: 2021,
      link: "https://doi.org/10.1007/s12039-021-01999-8",
      authors: "Jaganathan J, Patel A, et al.",
      citations: 15
    }
  ],

  // Completed Projects
  projects: [
    {
      title: "Clinical Validation of Urinary Biomarkers for Diabetic Complications",
      description: "Multi-center clinical study validating new urinary biomarkers for early detection of diabetic nephropathy, involving over 500 patients across Tamil Nadu.",
      image: "/images/project1_chem.jpg",
      outcomes: [
        "Validated 3 novel biomarkers",
        "Improved early diagnosis rates by 30%",
        "Collaboration with 4 hospitals",
        "Results published in peer-reviewed journal"
      ],
      year: 2023,
      link: "https://www.drmohans.com/research/biomarkers-study"
    },
    {
      title: "Development of Low-Cost Glucose Assay Kit",
      description: "Designed and developed a colorimetric glucose assay kit for rapid blood sugar testing, now used in several clinics in South India.",
      image: "/images/project2_chem.jpg",
      outcomes: [
        "Kit adopted by 20+ clinics",
        "Reduced testing costs by 40%",
        "Received innovation grant from DST",
        "Presented at national conference"
      ],
      year: 2022,
      link: "https://www.drmohans.com/research/glucose-assay-kit"
    },
    {
      title: "Synthesis of Antidiabetic Heterocycles",
      description: "Synthesized and screened a library of heterocyclic compounds for antidiabetic activity in collaboration with pharmaceutical partners.",
      image: "/images/project3_chem.jpg",
      outcomes: [
        "Identified 2 lead compounds",
        "Patent application filed",
        "Joint publication with pharma partner",
        "Potential for clinical trials"
      ],
      year: 2021,
      link: "https://www.drmohans.com/research/antidiabetic-heterocycles"
    }
  ],

  // Call-to-Action (CTA) Section
  callToAction: {
    message: "Collaborate or Connect for Research & Guidance",
    details: "I welcome students and researchers in Chennai and across India to connect for PhD projects, internships, or collaborative research in Chemistry and healthcare.",
    opportunities: [
      "PhD Guidance in Chemistry",
      "Research Internships",
      "Clinical Collaborations",
      "Workshops & Seminars",
      "Mentoring for Young Scientists",
      "Industry-Academia Partnerships"
    ]
  }
};

export default profileConfig; 